import React from 'react';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { throttle } from 'lodash';
import { useIntl } from 'react-intl';

import { GET_PAY_STATION_STATUS_RESOURCE } from 'src/features/monitoring/api/useGetPayStationStatus';
import {
    LIST_MONITORING_HEALTH_PAY_STATIONS_RESOURCE,
    useListMonitoringHealthPayStations,
} from 'src/features/monitoring/api/useListMonitoringHealthPayStations';
import MonitoringTabRow from 'src/features/monitoring/components/MonitoringTabRow';
import SingleMonitoringPayStation from 'src/features/monitoring/components/SingleMonitoringPayStation';
import { parkingStatusMessages } from 'src/features/monitoring/utils/messages';
import { useListPayStations } from 'src/features/payment/api/useListPayStations';
import { PayStationPaymentType } from 'src/features/payment/utils/types';
import WSContext, { WebSocketGroup } from 'src/features/visitor/context/WSContext';
import { HealthSubjectKind, WebSocketEvent, WebSocketEventMessages } from 'src/features/visitor/types';
import { queryClient } from 'src/lib/react-query';
import { useParking } from 'src/providers/ParkingProvider';
import { SectionBlock, SectionBlockBody } from 'src/registry/components/blocks/SectionBlock';
import { Button } from 'src/registry/ui/Button';
import { Skeleton } from 'src/registry/ui/Skeleton';
import { ICON_MAP } from 'src/utils/constants';

export default function MonitoringPayStations() {
    const { formatMessage } = useIntl();
    const { parkingId } = useParking();
    const [isOpen, setIsOpen] = React.useState(true);

    const { subscribe, unsubscribe } = React.useContext(WSContext);
    const { data, isLoading } = useListMonitoringHealthPayStations({ parkingId, limit: 100 });

    // Lifted here to avoid N+1 calls — each SingleMonitoringPayStation only needs
    // to know whether cash is enabled, which we derive once from the shared list.
    const { data: payStationsList } = useListPayStations({ parkingId, limit: 100 });
    const cashEnabledById = React.useMemo(() => {
        const map = new Map<string, boolean>();
        payStationsList?.results?.forEach((ps) => {
            map.set(ps.id, ps.settings.enabled_payment_methods.includes(PayStationPaymentType.CASH));
        });
        return map;
    }, [payStationsList]);

    const handlePayStationWebSocket = React.useMemo(
        () =>
            throttle((event: WebSocketEvent) => {
                if (
                    event.message === WebSocketEventMessages.PAY_STATION_SCREEN_CHANGE ||
                    event.message === WebSocketEventMessages.PAY_STATION_OPERATION_CHANGE ||
                    event.message === WebSocketEventMessages.PAY_STATION_CASH_LEVEL_CHANGE ||
                    event.message === WebSocketEventMessages.NEW_PAY_STATION_EVENT ||
                    event.message === WebSocketEventMessages.NEW_PAY_STATION_TRANSACTION
                ) {
                    void queryClient.invalidateQueries({
                        queryKey: [GET_PAY_STATION_STATUS_RESOURCE, event.data.pay_station_id],
                    });
                }
            }, 500),
        [],
    );

    const handleHealthWebSocket = React.useMemo(
        () =>
            throttle((event: WebSocketEvent) => {
                if (
                    event.message === WebSocketEventMessages.HEALTH_STATUS_UPDATED &&
                    event.data.subject.kind === HealthSubjectKind.PAY_STATION
                ) {
                    void queryClient.invalidateQueries({
                        queryKey: [LIST_MONITORING_HEALTH_PAY_STATIONS_RESOURCE],
                    });
                }
            }, 500),
        [],
    );

    React.useEffect(() => {
        subscribe(WebSocketGroup.PAY_STATION, handlePayStationWebSocket);
        subscribe(WebSocketGroup.MONITORING_HEALTH, handleHealthWebSocket);

        return () => {
            unsubscribe(WebSocketGroup.PAY_STATION, handlePayStationWebSocket);
            unsubscribe(WebSocketGroup.MONITORING_HEALTH, handleHealthWebSocket);
        };
    }, [handlePayStationWebSocket, handleHealthWebSocket, subscribe, unsubscribe]);

    return (
        <>
            <div className="flex items-center justify-between">
                <MonitoringTabRow />
                <div className="flex justify-end">
                    <Button variant="outline" onClick={() => setIsOpen((prevState) => !prevState)}>
                        <FontAwesomeIcon icon={isOpen ? ICON_MAP.COMPRESS : ICON_MAP.EXPAND} />
                        {formatMessage(isOpen ? parkingStatusMessages.collapseAll : parkingStatusMessages.expandAll)}
                    </Button>
                </div>
            </div>
            <SectionBlock>
                <SectionBlockBody className="grid md:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4">
                    {isLoading &&
                        Array.from({ length: 3 }).map((_, index) => (
                            <Skeleton key={index} className="h-160 rounded-xl" />
                        ))}
                    {data?.results.map((payStation) => (
                        <SingleMonitoringPayStation
                            key={payStation.id}
                            payStation={payStation}
                            isOpen={isOpen}
                            isCashEnabled={cashEnabledById.get(payStation.id) ?? true}
                        />
                    ))}
                </SectionBlockBody>
            </SectionBlock>
        </>
    );
}
